"use client"

import { Tab } from "@headlessui/react"
import clsx from "clsx"
import Image from "next/image"
import { useEffect, useState } from "react"

import { Analyse } from "domains/home/components/PrimaryFeatures/Analyse"
import { Explore } from "domains/home/components/PrimaryFeatures/Explore"
import { Export } from "domains/home/components/PrimaryFeatures/Export"
import { Upload } from "domains/home/components/PrimaryFeatures/Upload"
import { Container } from "./Container"
import backgroundImage from "../images/background-features.webp"

const features = [
    {
        title: "Upload",
        description: "Upload an Excel or CSV exported from your data collection platform.",
        image: <Upload />,
    },
    {
        title: "Analyse",
        description: "Sit tight for a few minutes while we analyse your data. Why not grab a coffee.",
        image: <Analyse />,
    },
    {
        title: "Explore",
        description:
            "Dive in and examine how we have analysed your data, what themes we have identified and insights we have found.",
        image: <Explore />,
    },
    {
        title: "Export",
        description:
            "Ready to share the results with others, just click export and to generate either a presentation or 2-page report.",
        image: <Export />,
    },
]

export function PrimaryFeatures() {
    const [tabOrientation, setTabOrientation] = useState<"horizontal" | "vertical">("horizontal")

    useEffect(() => {
        const lgMediaQuery = window.matchMedia("(min-width: 1024px)")

        function onMediaQueryChange({ matches }: { matches: boolean }) {
            setTabOrientation(matches ? "vertical" : "horizontal")
        }

        onMediaQueryChange(lgMediaQuery)
        lgMediaQuery.addEventListener("change", onMediaQueryChange)

        return () => {
            lgMediaQuery.removeEventListener("change", onMediaQueryChange)
        }
    }, [])

    return (
        <section
            id="features"
            aria-label="Features for running your books"
            className="relative overflow-hidden bg-primary-600 pb-28 pt-20 sm:py-32"
        >
            <Image
                className="absolute left-1/2 top-1/2 max-w-none translate-x-[-44%] translate-y-[-42%] brightness-90"
                src={backgroundImage}
                alt=""
                width={2245}
                height={1636}
                unoptimized
            />
            <Container className="relative">
                <div className="max-w-2xl md:mx-auto md:text-center xl:max-w-none">
                    <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl md:text-5xl">
                        The fastest way to get insights from a data file.
                    </h2>
                    <p className="mt-6 text-lg tracking-tight text-primary-100">
                        ResearchWiseAI is your automated assistant that reads and summarises market research data files.
                    </p>
                </div>
                <Tab.Group
                    as="div"
                    className="mt-16 grid grid-cols-1 items-center gap-y-2 pt-10 sm:gap-y-6 md:mt-20 lg:grid-cols-12 lg:pt-0"
                    vertical={tabOrientation === "vertical"}
                >
                    {({ selectedIndex }) => (
                        <>
                            <div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
                                <Tab.List className="relative z-10 flex gap-x-4 whitespace-nowrap px-4 sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                                    {features.map((feature, featureIndex) => (
                                        <div
                                            key={feature.title}
                                            className={clsx(
                                                "group relative rounded-full px-4 py-1 lg:rounded-l-xl lg:rounded-r-none lg:p-6",
                                                selectedIndex === featureIndex
                                                    ? "bg-white lg:bg-white/10 lg:ring-1 lg:ring-inset lg:ring-white/10"
                                                    : "hover:bg-white/10 lg:hover:bg-white/5"
                                            )}
                                        >
                                            <h3>
                                                <Tab
                                                    className={clsx(
                                                        "font-display text-lg ui-not-focus-visible:outline-none",
                                                        selectedIndex === featureIndex
                                                            ? "text-primary-600 lg:text-white"
                                                            : "text-primary-100 hover:text-white lg:text-white"
                                                    )}
                                                >
                                                    <span className="absolute inset-0 rounded-full lg:rounded-l-xl lg:rounded-r-none" />
                                                    {feature.title}
                                                </Tab>
                                            </h3>
                                            <p
                                                className={clsx(
                                                    "mt-2 hidden text-sm lg:block",
                                                    selectedIndex === featureIndex
                                                        ? "text-white"
                                                        : "text-primary-100 group-hover:text-white"
                                                )}
                                            >
                                                {feature.description}
                                            </p>
                                        </div>
                                    ))}
                                </Tab.List>
                            </div>
                            <Tab.Panels className="lg:col-span-7">
                                {features.map((feature) => (
                                    <Tab.Panel key={feature.title} unmount={false}>
                                        <div className="relative sm:px-6 lg:hidden">
                                            <div className="absolute -inset-x-4 bottom-[-4.25rem] top-[-6.5rem] bg-white/10 ring-1 ring-inset ring-white/10 sm:inset-x-0 sm:rounded-t-xl" />
                                            <p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
                                                {feature.description}
                                            </p>
                                        </div>
                                        <div className="mt-10 w-[45rem] overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-primary-900/20 sm:w-auto lg:mt-0 lg:w-[67.8125rem]">
                                            {feature.image}
                                        </div>
                                    </Tab.Panel>
                                ))}
                            </Tab.Panels>
                        </>
                    )}
                </Tab.Group>
            </Container>
        </section>
    )
}
